import React, { FC, useCallback, useContext, useEffect, useRef } from 'react';

import { useEventEmitter } from '@src/hooks/useEventEmitter';
import { UnitGlobalContext } from './context';
import { IStackRow, IUnitEventData } from './models';
import { send } from './utils';
import { getCenLead, getUTCTimestamp, handleCenUnitHit } from '@src/utils/analytics';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { IComponentChildrenProps } from '@src/models/props';
import { EVENTS_NAME } from '@src/consts/common';
import { getOrSetCentraCid } from '@src/utils/centraCid';
import { userEffectorModel } from '@src/stores/user';
import { useUnit } from 'effector-react';
import { AppContext } from '@src/providers/App/AppContext';
import { getUUID } from '@src/utils/uuid';
import { AnyType } from '@src/models/common';

interface IProps extends IComponentChildrenProps {
  // Идентификатор клиента (cid) — это уникальный идентификатор пары браузер-устройство,
  // который помогает аналитику связать действия пользователя на сайте.
  // Если не указан - получаем автоматически рассчитанное и сохраненное в cookie значение
  cid?: string;
  // ID авторизорванного пользователя
  // Ни в коем случае тут не должно быть email-а и прочих личных данных
  userId?: string;
  // Раздел сайта
  bundleId: string;
  // Можно указать тут "debug", чтобы просто вывести логи в консоль
  // без отправки запросов на бекенд
  statUrl: string;
}

export const UnitGlobal: FC<IProps> = ({ bundleId, cid, userId, statUrl, children }) => {
  // Свою куку проставляем все равно
  const cookieCid = getOrSetCentraCid();
  const currentCid = cid ? cid : cookieCid;
  const isDebugMode = statUrl === 'debug';
  const { breakpoint } = useContext(BreakpointsContext);
  const { isAuthZone } = useContext(AppContext);
  const { $userStore: user } = useUnit(userEffectorModel);

  if (!userId && user) {
    userId = String(user.id);
  }

  const buildEmptyStackRow = useCallback((): IStackRow => {
    return { cid: currentCid, bp: breakpoint, bundleId, isAuthZone, userId, records: [] };
  }, [currentCid, breakpoint, bundleId, userId, isAuthZone]);

  const emitter = useEventEmitter();
  // Текущая переменная, которую мы будем наполнять текущими новыми событиями
  const currentStackRow = useRef<IStackRow>(buildEmptyStackRow());
  // Стек инициализируем currentStackRow чтобы данные сразу в нем копились.
  // Далее без блокировок будем подменять currentStackRow для новых событий,
  // и отправлять на бек то, куда больше не пишем новое
  const stack = useRef<IStackRow[]>([currentStackRow.current]);

  const handleSendToBackend = useCallback(() => {
    if (currentStackRow.current.bp === undefined) {
      // Не всегда при первичной загрузке есть уже посчитанные брейкпоинты
      currentStackRow.current.bp = breakpoint;
    }
    if (currentStackRow.current.records.length > 0) {
      currentStackRow.current = buildEmptyStackRow();
      stack.current.push(currentStackRow.current);
    }

    if (stack.current.length > 1) {
      // Берем из начала стека, но не тот элемент, который сейчас
      // currentStackRow, так как в него push-аться новые события,
      // пока мы тут пытаемся отправить даные на бек
      const rowToSend = stack.current[0];
      send({ isDebugMode, statUrl, rowToSend }).then(() => {
        // Удялаем сначала только если все прошло хорошо.
        // Иначе надо подождать следующего раунда
        stack.current.shift();
      });
    }
  }, [breakpoint, buildEmptyStackRow, isDebugMode, statUrl]);

  const handleEvent = useCallback(
    (record: IUnitEventData) => {
      if (isDebugMode) {
        console.warn('UnitEventSender handleEvent', record);
      }

      if (currentStackRow.current.records.length > 1000) {
        // Защита от непонятных переполнений, чтоб не завис браузер,
        // если вдруг мы не можем что-то отправить на бек
        return;
      }

      if (typeof window !== 'undefined') {
        try {
          record.id = getUUID();
          record.location = window.location.href;
          record.referrer = document.referrer;
          record.cenLead = getCenLead();
          record.t = getUTCTimestamp();
          record.effectiveType = (window.navigator as AnyType).connection?.effectiveType;
        } catch (e) {
          console.error(e);
        }
      }
      currentStackRow.current.records.push(JSON.parse(JSON.stringify(record)));
      if (record.details.kind === 'click') {
        // при событии клика надо сразу попытаться все отправить на бек
        // так как иначе припереходе между страницами можно потерять данные в памяти
        handleSendToBackend();
      }

      // Толкаем что-то типа мидлвари для аналитиков.
      // Подробнсти читай в функции.
      // Вызов на всякий случай оставляем в самом конце после основного кода
      handleCenUnitHit(record);
    },
    [handleSendToBackend, isDebugMode],
  );

  useEffect(() => {
    if (isDebugMode) {
      console.warn('Start UnitEventSender in debug mode!!!');
    }

    emitter.on(EVENTS_NAME.UNIT_EVENT_LOG, handleEvent);
    const intervalId = setInterval(handleSendToBackend, 3000);

    return () => {
      emitter.off(EVENTS_NAME.UNIT_EVENT_LOG, handleEvent);
      clearInterval(intervalId);
    };
  }, [emitter, handleEvent, handleSendToBackend, isDebugMode]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('unload', handleSendToBackend, false);

      return () => {
        window.addEventListener('unload', handleSendToBackend);
      };
    }
  }, [handleSendToBackend]);

  return <UnitGlobalContext.Provider value={{}}>{children}</UnitGlobalContext.Provider>;
};
